import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, AlertCircle, CheckCircle2, ListTodo, ChevronRight } from 'lucide-react';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ToDoSummary = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get current week start date
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - now.getDay() + 1);
      const weekStart = monday.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/tasks`, {
        params: { week: weekStart },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter incomplete todos and sort by due date
      const incompleteTodos = (response.data.tasks || [])
        .filter(task => !task.completed)
        .sort((a, b) => {
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date) - new Date(b.due_date);
        });
      
      setTodos(incompleteTodos.slice(0, 5)); // Show max 5 todos
    } catch (error) {
      console.error('Error fetching todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return { label: 'No due date', color: 'text-gray-500 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600' };
    
    const daysUntil = getDaysUntilDue(dueDate);
    
    if (daysUntil < 0) {
      return { 
        label: `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'}`,
        color: 'text-red-700 dark:text-red-300',
        bgColor: 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800',
        icon: <AlertCircle className="w-4 h-4" />
      };
    } else if (daysUntil === 0) {
      return { 
        label: 'Due today!',
        color: 'text-orange-700 dark:text-orange-300',
        bgColor: 'bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800',
        icon: <AlertCircle className="w-4 h-4" />
      };
    } else if (daysUntil === 1) {
      return { 
        label: 'Due tomorrow',
        color: 'text-yellow-700 dark:text-yellow-300',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800',
        icon: <Clock className="w-4 h-4" />
      };
    } else if (daysUntil <= 3) {
      return { 
        label: `Due in ${daysUntil} days`,
        color: 'text-yellow-700 dark:text-yellow-300',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800',
        icon: <Clock className="w-4 h-4" />
      };
    } else {
      return { 
        label: `Due in ${daysUntil} days`,
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600',
        icon: <Clock className="w-4 h-4" />
      };
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'border-l-red-500';
      case 'normal': return 'border-l-blue-500';
      case 'low': return 'border-l-gray-400';
      default: return 'border-l-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="text-center py-4">
          <div className="inline-block w-8 h-8 border-4 border-cyan-500 dark:border-cyan-400 border-t-transparent rounded-full animate-spin transition-colors"></div>
        </div>
      </div>
    );
  }

  if (todos.length === 0) {
    return null; // Don't show widget if no todos
  }

  return (
    <div className="mb-6">
      <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg transition-colors">
        <div className="p-4 sm:p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                <ListTodo className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 transition-colors">Upcoming To-Dos</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors">{todos.length} pending task{todos.length === 1 ? '' : 's'}</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/calendar?todo=panel')}
              className="flex items-center gap-1 text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium transition-colors"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* To-Do List */}
          <div className="space-y-3">
            {todos.map((todo) => {
              const status = getDueDateStatus(todo.due_date);
              return (
                <div 
                  key={todo.id}
                  className={`border-l-4 ${getPriorityColor(todo.priority)} bg-gradient-to-r from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-r-lg p-3 hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 dark:border-gray-600`}
                  onClick={() => navigate(`/calendar?todo=${todo.id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm truncate transition-colors">{todo.title}</h4>
                      {todo.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-1 transition-colors">{todo.description}</p>
                      )}
                    </div>
                    {todo.due_date && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color} flex-shrink-0 transition-colors`}>
                        {status.icon}
                        <span>{status.label}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Priority Badge */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                      todo.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800' :
                      todo.priority === 'normal' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                    }`}>
                      {todo.priority} priority
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 transition-colors">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xl font-bold text-red-600 dark:text-red-400 transition-colors">
                  {todos.filter(t => getDaysUntilDue(t.due_date) < 0).length}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">Overdue</div>
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600 dark:text-orange-400 transition-colors">
                  {todos.filter(t => {
                    const days = getDaysUntilDue(t.due_date);
                    return days === 0;
                  }).length}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">Due Today</div>
              </div>
              <div>
                <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400 transition-colors">
                  {todos.filter(t => {
                    const days = getDaysUntilDue(t.due_date);
                    return days > 0 && days <= 3;
                  }).length}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">Due Soon</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ToDoSummary;
